// Caminho absoluto: src/components/layout/StageAmbience.tsx

import { useMemo } from 'react';
import { getBiomeTheme } from '../../utils/biomeTheme';

/**
 * StageAmbience — a "moldura viva" da engine. Ocupa a viewport inteira ATRÁS do
 * jogo (fixed inset-0, -z-10, pointer-events-none) e transforma o espaço que
 * sobra em telas grandes de "vazio" em "mundo de jogo": vinheta de foco, dois
 * orbes de glow na cor do bioma com drift lento, grade sci-fi e poeira estelar.
 *
 * 100% CSS. Zero JS no loop de animação. `prefers-reduced-motion` desliga todo
 * o movimento (keyframes em index.css usam @media reduce). Nenhuma mecânica é
 * tocada — é puramente ambiental e decorativa (aria-hidden).
 *
 * A cor e a intensidade vêm de `getBiomeTheme(fase)`: cada Ciclo é um bioma com
 * cor-assinatura (âmbar→ciano→índigo→violeta→púrpura) e, dentro do ciclo, a
 * intensidade sobe da 1ª à última fase — 50 telas distintas, mas coerentes.
 */

interface Props {
  fase: number;
}

export function StageAmbience({ fase }: Props) {
  const tema = useMemo(() => getBiomeTheme(fase), [fase]);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden transition-[filter] duration-700"
      style={{ ['--amb' as string]: tema.rgb, ['--amb-i' as string]: tema.intensity }}
    >
      {/* Grade sci-fi — dá "chão" ao mundo. Cobre a tela inteira; só as bordas
          extremas desvanecem, então em telas grandes o entorno tem textura em
          vez de preto morto. */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(148,163,184,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.7) 1px, transparent 1px)',
          backgroundSize: 'clamp(30px, 6vmin, 58px) clamp(30px, 6vmin, 58px)',
          maskImage: 'radial-gradient(140% 130% at 50% 50%, black 55%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(140% 130% at 50% 50%, black 55%, transparent 100%)',
        }}
      />

      {/* Orbe superior — glow do bioma, drift lento em diagonal. A alpha é
          multiplicada pela intensidade do ciclo (fases finais mais carregadas). */}
      <div
        className="amb-orb amb-orb-a absolute rounded-full"
        style={{
          width: '80vmax',
          height: '80vmax',
          top: '-32vmax',
          left: '-26vmax',
          background: `radial-gradient(circle, rgba(var(--amb), calc(0.22 * var(--amb-i))) 0%, rgba(var(--amb), calc(0.08 * var(--amb-i))) 34%, transparent 66%)`,
        }}
      />

      {/* Orbe inferior — mesma cor do bioma (mais presença embaixo, onde antes
          havia void em telas altas), drift oposto */}
      <div
        className="amb-orb amb-orb-b absolute rounded-full"
        style={{
          width: '78vmax',
          height: '78vmax',
          bottom: '-30vmax',
          right: '-24vmax',
          background:
            `radial-gradient(circle, rgba(var(--amb), calc(0.16 * var(--amb-i))) 0%, rgba(var(--amb), calc(0.06 * var(--amb-i))) 40%, transparent 72%)`,
        }}
      />

      {/* Vinheta de foco: SUTIL — só escurece as bordas extremas para puxar o
          olho ao palco, sem transformar o entorno em buraco preto. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(135% 110% at 50% 45%, transparent 55%, rgba(2,6,23,0.4) 85%, rgba(2,6,23,0.62) 100%)',
        }}
      />

      {/* Poeira estelar — pontos dispersos com cintilar muito lento */}
      <div className="amb-dust absolute inset-0" />
    </div>
  );
}
